import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { cn } from "@/utils/cn";
import { useUserByEmail } from "@/services/settings";
import deloitteLogo from "@/imgs/Deloitte Logo.svg";
import questionIcon from "@/imgs/Question.png";
import { env } from "@/config/env";
import { useFiscalYears } from "@/services/draftCalendar";
import { getAllowedNavItems, isApproverRole, shouldSkipDashboardForUser } from "@/app/navigation";

export function Header() {
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts, instance } = useMsal();
  const email = accounts[0]?.username ?? "";

  const { data: user } = useUserByEmail(email, {
    enabled: Boolean(email),
  });
  
  const { data: fiscalYears = [], isPending: loadingFY } = useFiscalYears();
  const userHasApproverRole = isApproverRole(user);
  const shouldSkipDashboard = shouldSkipDashboardForUser(user, fiscalYears, loadingFY);
  const allowedNavItems = getAllowedNavItems(user, shouldSkipDashboard);
  const nextAllowedPath = allowedNavItems[0]?.href;
  const userGuideUrl = userHasApproverRole
    ? (env.VITE_APPROVER_USER_GUIDE_URL ?? "")
    : (env.VITE_ADMIN_USER_GUIDE_URL ?? "");

  const profileInitial = (user?.firstName?.[0] ?? user?.name?.[0] ?? "M").toUpperCase();

  useEffect(() => {
    const isDashboardPath =
      location.pathname === "/" ||
      location.pathname === "/dashboard" ||
      location.pathname.startsWith("/dashboard/");

    if (shouldSkipDashboard && isDashboardPath && nextAllowedPath) {
      navigate(nextAllowedPath, { replace: true });
    }
  }, [location.pathname, navigate, nextAllowedPath, shouldSkipDashboard]);

  function handleLogOut() {
    void instance.logoutRedirect();
  }

  return (
    <header className="app-header-react">
      <div className="menu-left-react">
        <div className="logo-menu-react">
          <img
            src={deloitteLogo}
            alt="Deloitte"
            className="deloitte-logo-react"
          />
        </div>
        <div className="application-menu-react">
                    <div className="application-sub-title-container-react">
            <div className="application-sub-title-react">
              <span className="bar">|</span>
              
            </div>
          </div>
          <nav className="header-nav-react" aria-label="Primary">
            {allowedNavItems.map((item) => {
              const isActive =
                (item.href === "/dashboard"
                  ? location.pathname === "/" || location.pathname.startsWith("/dashboard")
                  : location.pathname === item.href ||
                    location.pathname.startsWith(`${item.href}/`));

              return (
                <NavLink key={item.label} to={item.href} className="header-tab-link">
                  <span
                    className={cn(
                      "header-tab-menu-container-react",
                      isActive && "active-link-text-react"
                    )}
                  >
                    <span className="header-tab-title-react">{item.label}</span>
                  </span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="menu-right-react">
        <div className="header-container-react">
          <div className="question-menu-react">
            <button
              type="button"
              className="icon-button-react"
              onClick={() => {
                setShowHelpMenu((open) => !open);
                setShowUserMenu(false);
              }}
              aria-expanded={showHelpMenu}
              aria-haspopup="menu"
              aria-label="Have a question"
            >
              <div className="logo" style={{ justifyContent: "center" }}>
                <img style={{ height: "35px", paddingLeft: "10px" }} src={questionIcon} alt="" />
              </div>
            </button>
            {showHelpMenu ? (
              <div className="question-dropdown-react" role="menu">
                <a target="_blank" rel="noopener noreferrer" href={userGuideUrl}>
                  User Guide
                </a>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={env.VITE_LOG_ISSUE_URL ?? ""}
                >
                  Log an Issue
                </a>
              </div>
            ) : null}
          </div>

          <div className="user-menu-anchor-react">
            <button
              type="button"
              className="profile-button-react"
              onClick={() => {
                setShowUserMenu((open) => !open);
                setShowHelpMenu(false);
              }}
              aria-expanded={showUserMenu}
              aria-haspopup="menu"
              aria-label="User menu"
            >
              <span className="profile-avatar-react">{profileInitial}</span>
            </button>
            {showUserMenu ? (
              <div className="user-menu-react" role="menu">
                <button type="button" className="user-menu-item-react" onClick={handleLogOut}>
                  Log Out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
